import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { IonicModule, ModalController, PopoverController } from '@ionic/angular';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { Chart } from 'chart.js';
import Markdown from 'markdown-it';
import moment from 'moment-timezone';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { DatetimeModalComponent } from 'src/app/components/datetime-modal/datetime-modal.component';
import { CONVERSATION_STATUS, DATE_OPTIONS, DATE_RANGE_TYPES } from 'src/app/constants.js';
import { downloadCSV, getHtmlFromSlackMd } from 'src/app/utils/utils';
import { FetchDataRequest, FetchDataResponse, createDataSource } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { AiResponse, WhereClause } from 'src/models';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

interface ConversationCountCall {
  baseStatuses: {
    open: number;
    inProgress: number;
    closed: number;
    draft: number;
    onHold: number;
  };
  deflectedStatuses: {
    stale?: number;
    closedBySubmitter?: number;
    closedByInternalUser?: number;
    closedByExternalUser?: number;
  };
}

@Component({
  standalone: true,
  selector: 'app-ai-analytics',
  templateUrl: './ai-analytics.component.html',
  styleUrls: ['./ai-analytics.component.scss'],
  imports: [
    CommonModule,
    SharedDirectivesModule,
    FormsModule,
    IonicModule,
    RouterModule,
    NgxTippyModule,
    NgxDatatableModule,
  ],
})
export class AiAnalyticsComponent implements OnInit {
  @ViewChild('chart') chart: Chart;

  filterType: 'all' | 'thumbs_up' | 'thumbs_down' | 'no_response' | 'has_response' = 'all';
  feedbackItems = [];
  isInitialized = false;
  isLoading: boolean;
  allAiResponses: number;
  deflectedAiResponses: number;
  dateRange = DATE_OPTIONS[1];
  draft = 0;
  readonly draftStatus = CONVERSATION_STATUS.INTAKE;
  inProgress = 0;
  open = 0;
  closed = 0;
  isDateRangeInvalid: boolean;
  startDate: Date | null;
  endDate: Date | null;
  timezone: string;
  limit = 50;
  cursor: any;
  sortField = 'createdAt';
  sortDesc = true;

  constructor(
    private apiService: ApiService,
    private popoverCtrl: PopoverController,
    private analyticsService: AnalyticsService,
    private modalCtrl: ModalController,
    private searchablePopoverService: SearchablePopoverService,
  ) {}

  ngOnInit() {
    this.isInitialized = false;

    this.loadResponses();

    this.timezone = moment.tz.guess();
    this.endDate = moment().toDate();
    this.startDate = moment(this.endDate).startOf('day').subtract(7, 'days').toDate();

    this.getConversationResponses();
  }

  renderMarkdown(markdown: string) {
    const md = new Markdown('commonmark', {
      xhtmlOut: true,
      breaks: true,
      html: true,
    });

    const html = md.render(markdown);

    return html;
  }

  getDateRange() {
    const params: any = {
      timezone: this.timezone,
    };

    if (this.dateRange.type === DATE_RANGE_TYPES.RELATIVE) {
      // Unlike with standard reporting, we want this to include the current day
      params.dateRange = {
        ...this.dateRange,
        endIndex: 0,
      };
    } else {
      params.dateRange = {
        type: DATE_RANGE_TYPES.ABSOLUTE,
        startDate: moment(this.startDate).format('YYYY-MM-DD'),
        endDate: moment(this.endDate).format('YYYY-MM-DD'),
      };
    }

    return params;
  }

  async changeDate(isEnd: boolean) {
    const datePickerModal = await this.modalCtrl.create({
      component: DatetimeModalComponent,
      cssClass: 'datetime-modal',
      componentProps: {
        min: isEnd ? this.startDate : null,
        selected: isEnd ? this.endDate : this.startDate,
        title: isEnd ? 'Select end date' : 'Select start date',
      },
    });

    await datePickerModal.present();

    datePickerModal.onDidDismiss().then((data) => {
      const result = data?.data?.value;

      if (result && result !== 'Invalid date') {
        const newDate = moment(result).toDate();

        if (isEnd) {
          this.endDate = newDate;
        } else {
          this.startDate = newDate;
        }

        this.isDateRangeInvalid = this.startDate.getTime() > this.endDate.getTime();

        if (!this.isDateRangeInvalid) {
          this.getConversationResponses();
        }
      }
    });
  }

  async showDateRangeOptions() {
    this.searchablePopoverService.dateValues({
      event,
      callback: (id: string, _name: string, object: any) => {
        this.dateRange = object;

        this.isDateRangeInvalid =
          this.dateRange.type === DATE_RANGE_TYPES.ABSOLUTE && this.startDate.getTime() > this.endDate.getTime();

        if (!this.isDateRangeInvalid) {
          this.getConversationResponses();
        }
      },
    });
  }

  async getConversationResponses() {
    const dateData = this.getDateRange();
    await Promise.all([
      this.apiService
        .getPromise(
          `/reporting/conversation-count-by-status/?dateRange=${JSON.stringify(dateData.dateRange)}&timezone=${dateData.timezone}`,
        )
        .then((res: ConversationCountCall) => {
          res.baseStatuses.closed = res.baseStatuses.closed || 0;
          res.baseStatuses.inProgress = res.baseStatuses.inProgress || 0;
          res.baseStatuses.draft = res.baseStatuses.draft || 0;
          res.baseStatuses.open = res.baseStatuses.open || 0;
          res.baseStatuses.onHold = res.baseStatuses.onHold || 0;

          this.deflectedAiResponses = res.baseStatuses.draft;
          this.allAiResponses =
            res.baseStatuses.draft +
            res.baseStatuses.closed +
            res.baseStatuses.inProgress +
            res.baseStatuses.open +
            res.baseStatuses.onHold;
          this.isInitialized = true;
        })
        .catch((err) => {
          console.log('error: ', err);
        }),
      this.loadResponses(),
    ]);
  }

  async exportAsCsv() {
    // emit analytics events
    this.analyticsService.trackEvent('AI Analytics', 'Export CSV');
    // create date object
    const date = new Date();
    // retrieve and map data to headers
    // initial fetch, then loop over pages using cursor
    const pages = [];
    let cursors;

    do {
      const nextPage = await this.fetchResponses({
        limit: this.limit,
        order: [this.sortField],
        descending: this.sortDesc,
        cursor: cursors?.next,
      });

      pages.push(nextPage);
      cursors = nextPage.cursors;
    } while (cursors.hasNext);
    // flatten and format results
    const results = pages.flatMap((page) => {
      return page.data.map((row) => {
        return {
          query: row.query,
          aiResponse: row.response,
          userFeedback: JSON.stringify(row.feedbacks),
          customer: row.conversation?.customer?.name || null,
          ticketType: row.conversation?.ticketType?.name || null,
          created: row.createdAt,
        };
      });
    });
    // declare headers
    const headers = ['query', 'aiResponse', 'userFeedback', 'customer', 'ticketType', 'created'];
    // export combination as CSV
    downloadCSV(results, headers, `ai-analytics-${date.getTime()}`);
  }

  fetchResponses = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
  }: FetchDataRequest): Promise<FetchDataResponse<Partial<AiResponse>>> => {
    const options: any = {
      limit,
      order,
      descending,
      cursor,
    };

    const where: WhereClause[] = [];

    const { dateRange, timezone } = this.getDateRange();

    options.dateRange = dateRange;
    options.timezone = timezone;

    if (this.filterType === 'no_response') {
      where.push({
        field: 'response',
        operator: '==',
        value: '',
      });
    } else if (this.filterType === 'has_response') {
      where.push({
        field: 'response',
        operator: '!=',
        value: '',
      });
    }

    if (where.length) {
      options.where = where;
    }

    // TODO create a service
    let result = (await this.apiService.postPromise('/ai-responses/list', options)) as any;

    result.data = result.data.map((aiRes) => {
      return {
        ...aiRes,
        formattedQuery: aiRes.query ? getHtmlFromSlackMd(aiRes.query) : '',
        formattedResponse: aiRes.response ? this.renderMarkdown(aiRes.response) : '',
      };
    });

    return result;
  };

  responsesDateSource = createDataSource(this.fetchResponses);

  async loadResponses() {
    this.isLoading = true;

    await this.responsesDateSource.firstPage();

    this.isLoading = false;
  }
}
